// -----------------------------------------------------------------------------
// RecentlyCompletedCard
//
// Full-width animated completion banner shown for 4 s after the user completes
// a checklist action. Replaces the previous compact 30%-slot card (task #26).
//
// Visual approach:
//   • Full-width green-tint banner — fills whatever column the parent gives it.
//   • Scale-pop circle + path-draw check via framer-motion (m.div / m.path).
//   • 4 s scaleX countdown strip at the bottom — visual affordance for the
//     "next panel incoming" handoff. Exact timing matches the parent's
//     setTimeout(4000) but is independent (cosmetic-only).
//   • Dismiss button top-right retained.
//   • Reduced-motion: static layout, no animations.
//
// Props
//   item       — the checklist item that was just completed
//   label      — human-readable past-tense label ("Email sent", "Note added"…)
//   onDismiss  — callback to clear the transition state immediately
//   fillHeight — when true the card fills its containing box (used in overlay
//                context where the wrapper is position:absolute; inset:0). The
//                card root stretches to height:100%, content is vertically
//                centred, the countdown strip is pinned to the bottom edge, and
//                the check icon + padding are scaled up to match the up-next
//                card's visual weight. Standalone fallback keeps natural height.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useMediaQuery, Box, Typography, IconButton, Button } from '@mui/material';
import { m } from 'framer-motion';
import Iconify from 'src/components/Iconify';
import { success } from 'src/theme/palette';
import { ACTION_META, DEFAULT_ACTION_META } from './actionMeta';

// Colors — sourced from existing card tokens and theme.palette.success.*
const BG         = '#f0fdf4';           // existing card bg
const GREEN      = '#22c55e';           // = success.main
const TEXT_DARK  = '#166534';           // existing card label token
const TEXT_BODY  = '#1a1a1a';           // existing card title token
const TEXT_MUTED = '#86868b';           // existing card muted token
const BORDER     = success.lighter;     // #D3FCD2 — theme token, new element

const CARD_EASING = [0.32, 0.72, 0, 1];

export default function RecentlyCompletedCard({ item, label, onDismiss, onUndo, fillHeight }) {
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    if (!item) return null;

    const meta     = ACTION_META[item.item_type] || DEFAULT_ACTION_META;
    const title    = item.title || item.name || meta.label;
    const iconSize = fillHeight ? 56 : 40;

    return (
        <m.div
            data-testid="upnext-recently-completed-card"
            style={{
                position: 'relative',
                backgroundColor: BG,
                borderRadius: 16,
                border: `1px solid ${BORDER}`,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.04)',
                overflow: 'hidden',
                // fill mode: stretch to match the overlay container's height so
                // the card reads as an equal-sized sibling of UpNextCardV2.
                ...(fillHeight && {
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }),
            }}
            initial={reducedMotion ? false : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22, ease: CARD_EASING }}
        >
            {/* Content row — more breathing room in fill mode */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: fillHeight ? 3 : 2.5,
                py: fillHeight ? 3.5 : 2,
            }}>
                {/* Animated check — outer div pops in, inner path draws */}
                <m.div
                    style={{ display: 'flex', flexShrink: 0 }}
                    initial={reducedMotion ? false : { scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 18, delay: 0.08 }}
                >
                    <svg viewBox="0 0 40 40" width={iconSize} height={iconSize} aria-hidden="true">
                        <circle cx="20" cy="20" r="18" fill={GREEN} />
                        <m.path
                            d="M12 20.5 L17.5 26 L28 13.5"
                            fill="none"
                            stroke="#ffffff"
                            strokeWidth={2.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={
                                reducedMotion
                                    ? { duration: 0 }
                                    : { duration: 0.32, ease: 'easeOut', delay: 0.28 }
                            }
                        />
                    </svg>
                </m.div>

                {/* Text */}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        data-testid="upnext-recently-completed-label"
                        sx={{
                            fontSize: fillHeight ? 15 : 14,
                            fontWeight: 600,
                            color: TEXT_DARK,
                            lineHeight: 1.3,
                            mb: 0.5,
                        }}
                    >
                        Task completed
                    </Typography>
                    <Typography
                        data-testid="upnext-recently-completed-title"
                        sx={{
                            fontSize: fillHeight ? 14 : 13,
                            fontWeight: 500,
                            color: TEXT_BODY,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {title}
                    </Typography>
                    {label && label !== title && (
                        <Typography sx={{ fontSize: fillHeight ? 12 : 11, color: TEXT_MUTED, mt: 0.5 }}>
                            {label}
                        </Typography>
                    )}
                </Box>

                {/* Undo — reverses the completion (reopens the task) and clears the
                    ack card. Only rendered when the parent supplies an onUndo handler.
                    Sits before the dismiss X so it reads as the primary recovery action
                    within the 4 s window. */}
                {onUndo && (
                    <Button
                        data-testid="upnext-recently-completed-undo"
                        size="small"
                        onClick={onUndo}
                        startIcon={<Iconify icon="mdi:undo-variant" sx={{ fontSize: 16 }} />}
                        sx={{
                            flexShrink: 0,
                            color: TEXT_DARK,
                            fontWeight: 600,
                            fontSize: fillHeight ? 13 : 12,
                            textTransform: 'none',
                            borderRadius: '8px',
                            px: 1.25,
                            py: 0.5,
                            border: `1px solid ${BORDER}`,
                            bgcolor: '#ffffff',
                            '&:hover': { bgcolor: '#dcfce7', borderColor: GREEN },
                        }}
                        aria-label="Undo completion"
                    >
                        Undo
                    </Button>
                )}

                {/* Dismiss */}
                {onDismiss && (
                    <IconButton
                        data-testid="upnext-recently-completed-dismiss"
                        size="small"
                        onClick={onDismiss}
                        sx={{ flexShrink: 0, color: TEXT_MUTED }}
                        aria-label="Dismiss confirmation"
                    >
                        <Iconify icon="mdi:close" sx={{ fontSize: 16 }} />
                    </IconButton>
                )}
            </Box>

            {/* 4 s countdown strip — shrinks left-to-right over exactly 4 s.
                In fill mode pinned to the bottom edge via absolute positioning
                (the root already has overflow:hidden for border-radius clipping).
                Skipped for reduced-motion. */}
            {!reducedMotion && (
                <m.div
                    aria-hidden="true"
                    style={{
                        height: 4,
                        backgroundColor: GREEN,
                        transformOrigin: 'left center',
                        ...(fillHeight && { position: 'absolute', bottom: 0, left: 0, right: 0 }),
                    }}
                    initial={{ scaleX: 1 }}
                    animate={{ scaleX: 0 }}
                    transition={{ duration: 4, ease: 'linear' }}
                />
            )}
        </m.div>
    );
}

RecentlyCompletedCard.propTypes = {
    item:       PropTypes.object.isRequired,
    label:      PropTypes.string.isRequired,
    onDismiss:  PropTypes.func,
    onUndo:     PropTypes.func,
    fillHeight: PropTypes.bool,
};

RecentlyCompletedCard.defaultProps = {
    onDismiss:  null,
    onUndo:     null,
    fillHeight: false,
};
